import React from 'react'
import { Box, FormControl, FormLabel, FormHelperText } from '@mui/material'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import { SxProps, Theme } from '@mui/material/styles'

export type MultipleCheckboxInputProps = {
    /**
     * The label content
     */
    label?: string
    /**
     * The name used to reference the value of the control
     */
    name?: string
    /**
     * If `true`, the component is displayed in an error state.
     * @default false
     */
    error?: boolean
    /**
     * The helper text content
     */
    helperText?: React.ReactNode
    /**
     * If true, the input will take up the full width of its container.
     * @default false
     */
    fullWidth?: boolean
    /**
     * If `true`, the component is disabled.
     * @default false
     */
    disabled?: boolean
    /**
     * The options for the checkboxes
     */
    options: { label: string; value: string }[]
    /**
     * The selected values (array of selected option values)
     */
    value?: string[]
    /**
     * Callback fired when a checkbox is changed.
     */
    onChange?: (value: string[]) => void
    /**
     * The sx prop for styling
     */
    sx?: SxProps<Theme>
}

export function MultipleCheckboxInput(props: MultipleCheckboxInputProps) {
    const {
        label,
        name,
        error = false,
        helperText,
        fullWidth = false,
        disabled = false,
        options,
        value = [],
        onChange,
        sx,
    } = props

    const handleChange = (optionValue: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
        if (disabled) return

        const newValue = event.target.checked
            ? [...value, optionValue]
            : value.filter(v => v !== optionValue)

        onChange?.(newValue)
    }

    return (
        <FormControl
            component="fieldset"
            error={error}
            disabled={disabled}
            fullWidth={fullWidth}
            sx={sx}
        >
            {label && (
                <FormLabel component="legend" sx={{ mb: 1 }}>
                    {label}
                </FormLabel>
            )}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {options.map((option) => (
                    <FormControlLabel
                        key={option.value}
                        control={
                            <Checkbox
                                checked={value.includes(option.value)}
                                onChange={handleChange(option.value)}
                                name={`${name}-${option.value}`}
                                size="small"
                            />
                        }
                        label={option.label}
                    />
                ))}
            </Box>
            {helperText && <FormHelperText>{helperText}</FormHelperText>}
        </FormControl>
    )
}

export default MultipleCheckboxInput
